import { AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GetVirtualFenceService } from '@vectronic/admin-tools-services';
import { VirtualFenceService } from '@vectronic/base-map';
import { dsDtos, userDtos, webDtos } from '@vectronic/openApi-admin';
import { CardComponent } from '@vectronic/ui';
import { Observable, map } from 'rxjs';
import { CamerasFormatService } from '../../services/commands/cameras-format.service';
import { RemoteCommandProviderService } from '../../services/remote-command-provider.service';
import { ScheduleServices } from '../../services/schedule.service';
import { ScheduleViewComponent } from '../schedule-view/schedule-view.component';

@Component({
  selector: 'rc-schedule-modal',
  templateUrl: './schedule-modal.component.html',
  styleUrls: ['./schedule-modal.component.scss'],
  imports: [AsyncPipe, ScheduleViewComponent, CardComponent],
})
export class ScheduleModalComponent implements AfterViewInit {
  virtualFenceService = inject(VirtualFenceService);
  getVirtualFenceService = inject(GetVirtualFenceService);
  dialogRef = inject(MatDialogRef<ScheduleModalComponent>);
  scheduleServices = inject(ScheduleServices);
  camerasFormatService = inject(CamerasFormatService);
  remoteCommandProviderService = inject(RemoteCommandProviderService, { optional: true });

  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly initialDialogHeightPx = 450;

  data: {
    schedules: dsDtos.Schedule[];
    virtualFenceConstructionRules: dsDtos.VirtualFenceConstructionRule[];
    selectedCollarIds: number[] | undefined;
    shouldLoadVirtualFence?: boolean;
    isAdmin: boolean;
    hideVirtualFence?: boolean;
    isPositionsLoading?: boolean;
    mapPositions?: webDtos.MapPositionsList[];
    userPreferences?: userDtos.UserPreferences;
  } = inject(MAT_DIALOG_DATA);

  readonly videoFormatsOptions$ = this.camerasFormatService.videoFormatsOptions$;
  readonly photoFormatsOptions$ = this.camerasFormatService.photoFormatsOptions$;
  readonly isGsmSelected$: Observable<boolean> =
    this.remoteCommandProviderService?.selectedCollars$.pipe(
      map((collars) => {
        const selectedIds = this.data.selectedCollarIds;
        if (!collars || !selectedIds) return false;
        const selectedSet = new Set(selectedIds);
        return collars?.filter((c) => selectedSet.has(c.id))?.some((c) => c.comType === 'GSM');
      }),
    );

  readonly permanentEndDate$: Observable<string> = this.isGsmSelected$.pipe(
    map((isGsm) => (isGsm ? '2038-01-01' : '2050-01-01')),
  );

  constructor() {
    if (this.data.shouldLoadVirtualFence) {
      this.getVirtualFenceService.getVirtualFences(this.data.selectedCollarIds);
    }
    if (this.data.hideVirtualFence) {
      this.virtualFenceService.initialize([], false);
    }
    this.scheduleServices.initializeSchedules(this.data.schedules, false);
  }

  ngAfterViewInit() {
    const pane = this.hostElement.closest('.cdk-overlay-pane') as HTMLElement | null;
    if (pane && (pane.style.height === '' || pane.style.height === 'fit-content')) {
      pane.style.height = `${this.initialDialogHeightPx}px`;
    }
  }

  getDefaultVirtualFenceConstructionRules() {
    if (this.data.hideVirtualFence) return undefined;
    if (this.data.shouldLoadVirtualFence) {
      return this.virtualFenceService.constructionRules();
    }
    return this.data.virtualFenceConstructionRules;
  }

  get title() {
    return this.data.selectedCollarIds
      ? `Schedules for ${this.data.selectedCollarIds.join(',')}`
      : 'Schedules';
  }
}
